import React, { useContext, useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Alert, Clipboard } from 'react-native';
import { List, Divider, Switch, Text, Button, Dialog, Portal, ProgressBar, Snackbar, IconButton } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../navigation/RootNavigator';
import { FirebaseAuthContext } from '../context/FirebaseAuthContext';
import { useEmailInbox } from '../hooks/useEmailInbox';
import { auth } from '../services/firebase';

export default function SettingsScreen() {
  const authContext = useContext(FirebaseAuthContext);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { emailInboxId, emailAddress, generateEmailInboxId, copyEmailAddress, isGenerating, error } = useEmailInbox();
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState('');
  const [isLoadingCalendarUrl, setIsLoadingCalendarUrl] = useState(false);
  
  // Log component state for debugging
  useEffect(() => {
    console.log('Calendar Feed State:', {
      calendarFeedUrl,
      isLoadingCalendarUrl,
      showCalendarDialog
    });
  }, [calendarFeedUrl, isLoadingCalendarUrl, showCalendarDialog]);
  
  // Clear old calendar URL on mount to force regeneration
  useEffect(() => {
    // Check if the URL is using the old format
    if (calendarFeedUrl && calendarFeedUrl.includes('cloudfunctions.net')) {
      console.log('Clearing old calendar URL format');
      setCalendarFeedUrl('');
    }
  }, []);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  if (!authContext) {
    return null;
  }

  const { user, signOut } = authContext;

  // Track if we've already attempted to generate
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);

  const generateCalendarFeedUrl = async () => {
    console.log('Starting calendar feed URL generation...');
    setIsLoadingCalendarUrl(true);
    try {
      const currentUser = auth.currentUser;
      console.log('Current user:', currentUser?.uid);
      
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      const idToken = await currentUser.getIdToken();
      console.log('Got ID token');
      
      const functionsUrl = process.env.EXPO_PUBLIC_FIREBASE_FUNCTIONS_URL || 
        `https://us-central1-${process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
      
      console.log('Functions URL:', functionsUrl);
      
      try {
        const response = await fetch(`${functionsUrl}/getCalendarToken`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        });

        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Failed to generate calendar feed URL: ${response.status}`);
        }

        const data = await response.json();
        console.log('Calendar feed data:', data);
        setCalendarFeedUrl(data.feedUrl);
        return data.feedUrl;
      } catch (fetchError) {
        console.log('Function not deployed yet, using mock URL for testing');
        // Generate a mock URL for testing when functions aren't deployed
        const mockToken = btoa(currentUser.uid).substring(0, 16);
        const mockUrl = `${functionsUrl}/calendarFeed?userId=${currentUser.uid}&token=${mockToken}`;
        setCalendarFeedUrl(mockUrl);
        Alert.alert(
          'Calendar Feed Ready', 
          'Note: The calendar function needs to be deployed to Firebase to work. For now, you can see how the UI works.',
          [{ text: 'OK' }]
        );
        return mockUrl;
      }
    } catch (error: any) {
      console.error('Error generating calendar feed URL:', error);
      Alert.alert('Error', error.message || 'Failed to generate calendar feed URL');
      return null;
    } finally {
      setIsLoadingCalendarUrl(false);
    }
  };

  const copyCalendarUrl = async () => {
    if (calendarFeedUrl) {
      Clipboard.setString(calendarFeedUrl);
      return true;
    }
    return false;
  };

  // Automatically generate email ID for existing users who don't have one
  useEffect(() => {
    const setupEmailId = async () => {
      console.log('SettingsScreen useEffect:', {
        user: user?.id,
        emailInboxId,
        isGenerating,
        hasAttemptedGeneration
      });
      
      if (user && !emailInboxId && !isGenerating && !hasAttemptedGeneration) {
        console.log('Attempting to generate email ID...');
        setHasAttemptedGeneration(true);
        const result = await generateEmailInboxId();
        console.log('Generation result:', result);
      }
    };
    setupEmailId();
  }, [user?.id]); // Only depend on user.id to avoid re-runs

  return (
    <ScrollView style={styles.container}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Account</Text>
        <List.Item
          title={user?.name || 'Guest User'}
          description={user?.email || 'Not signed in'}
          left={(props) => <List.Icon {...props} icon="account" />}
        />
        <Divider />
        
        {/* Temporary Icon Test Button */}
        <Button mode="contained" onPress={() => navigation.navigate('IconTest' as any)}>
          Test Icons
        </Button>
        
        {user && (
          <>
            <List.Item
              title="Travel Email Address"
              description={
                emailInboxId 
                  ? `${emailInboxId}@inbound.tript.app` 
                  : isGenerating 
                    ? "Generating unique address..." 
                    : "Not set up yet"
              }
              left={(props) => <List.Icon {...props} icon="email-fast" />}
              right={() => 
                emailInboxId ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <IconButton
                      icon="content-copy"
                      size={20}
                      onPress={async () => {
                        const success = await copyEmailAddress();
                        if (success) {
                          setSnackbarMessage('Email address copied to clipboard!');
                        }
                      }}
                    />
                    <Button mode="text" onPress={() => setShowEmailDialog(true)}>
                      View
                    </Button>
                  </View>
                ) : isGenerating ? (
                  <ProgressBar indeterminate style={{ width: 50 }} />
                ) : (
                  <Button mode="text" onPress={async () => {
                    console.log('Manual generate button clicked');
                    const result = await generateEmailInboxId();
                    console.log('Manual generation result:', result);
                    if (result) {
                      setSnackbarMessage('Email address generated successfully!');
                    } else if (error) {
                      setSnackbarMessage(`Error: ${error}`);
                    }
                  }}>
                    Generate
                  </Button>
                )
              }
              onPress={() => emailInboxId ? setShowEmailDialog(true) : null}
            />
            {emailInboxId && (
              <Text style={styles.emailHelperText}>
                Use this email address to receive trip related information
              </Text>
            )}
            <Divider />
          </>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Preferences</Text>
        <List.Item
          title="Push Notifications"
          description="Receive trip reminders and updates"
          left={(props) => <List.Icon {...props} icon="bell" />}
          right={() => (
            <Switch
              value={notifications}
              onValueChange={setNotifications}
            />
          )}
        />
        <Divider />
        <List.Item
          title="Dark Mode"
          description="Enable dark theme"
          left={(props) => <List.Icon {...props} icon="theme-light-dark" />}
          right={() => (
            <Switch
              value={darkMode}
              onValueChange={setDarkMode}
            />
          )}
        />
        <Divider />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Integrations</Text>
        <List.Item
          title="Connect Gmail"
          description="Auto-import trip confirmations"
          left={(props) => <List.Icon {...props} icon="gmail" />}
          right={(props) => <List.Icon {...props} icon="chevron-right" />}
          onPress={() => {}}
        />
        <Divider />
        <List.Item
          title="Calendar Feed"
          description={calendarFeedUrl ? "Calendar feed active" : "Subscribe to your trips in any calendar app"}
          left={(props) => <List.Icon {...props} icon="calendar-sync" />}
          right={() => (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {calendarFeedUrl && (
                <Button 
                  mode="text" 
                  onPress={() => {
                    console.log('Regenerating calendar URL...');
                    setCalendarFeedUrl(''); // Clear existing URL
                    generateCalendarFeedUrl().then((url) => {
                      console.log('New URL generated:', url);
                      if (url) {
                        setShowCalendarDialog(true);
                      }
                    });
                  }}
                  disabled={isLoadingCalendarUrl}
                >
                  Regenerate
                </Button>
              )}
              <Button 
                mode="text" 
                onPress={() => {
                  console.log('Calendar button pressed!');
                  if (!calendarFeedUrl || calendarFeedUrl.includes('cloudfunctions.net')) {
                    // Generate new URL if none exists or if it's the old format
                    generateCalendarFeedUrl().then((url) => {
                      console.log('Generated URL:', url);
                      if (url) {
                        setShowCalendarDialog(true);
                      }
                    });
                  } else {
                    setShowCalendarDialog(true);
                  }
                }}
                disabled={isLoadingCalendarUrl}
              >
                {isLoadingCalendarUrl ? 'Loading...' : (calendarFeedUrl ? 'View' : 'Setup')}
              </Button>
            </View>
          )}
        />
        <Divider />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>About</Text>
        <List.Item
          title="Version"
          description="1.0.0"
          left={(props) => <List.Icon {...props} icon="information" />}
        />
        <Divider />
        <List.Item
          title="Privacy Policy"
          left={(props) => <List.Icon {...props} icon="shield-account" />}
          right={(props) => <List.Icon {...props} icon="chevron-right" />}
          onPress={() => {}}
        />
        <Divider />
        <List.Item
          title="Terms of Service"
          left={(props) => <List.Icon {...props} icon="file-document" />}
          right={(props) => <List.Icon {...props} icon="chevron-right" />}
          onPress={() => {}}
        />
        <Divider />
      </View>

      {user && (
        <View style={styles.signOutContainer}>
          <Button
            mode="outlined"
            onPress={signOut}
            style={styles.signOutButton}
          >
            Sign Out
          </Button>
        </View>
      )}

      {/* Email Setup Dialog */}
      <Portal>
        <Dialog visible={showEmailDialog} onDismiss={() => setShowEmailDialog(false)}>
          <Dialog.Title>Email Import Setup</Dialog.Title>
          <Dialog.Content>
            {emailInboxId ? (
              <>
                <Text>Your unique email address:</Text>
                <View style={styles.emailAddressContainer}>
                  <Text style={styles.emailAddress}>{emailInboxId}@inbound.tript.app</Text>
                  <IconButton
                    icon="content-copy"
                    size={20}
                    style={styles.copyIconInDialog}
                    onPress={async () => {
                      const success = await copyEmailAddress();
                      if (success) {
                        setSnackbarMessage('Email address copied to clipboard!');
                        setShowEmailDialog(false);
                      }
                    }}
                  />
                </View>
                <Text style={styles.helpText}>
                  Use this email address to receive trip related information. Forward your travel confirmations to this address and they'll appear in your inbox.
                </Text>
              </>
            ) : (
              <>
                <Text>
                  Set up a unique email address to forward your travel confirmations.
                </Text>
                <Text style={styles.helpText}>
                  Once generated, you'll be able to forward emails to this address.
                </Text>
              </>
            )}
            {isGenerating && <ProgressBar indeterminate style={styles.progressBar} />}
          </Dialog.Content>
          <Dialog.Actions>
            {emailInboxId ? (
              <>
                <Button onPress={async () => {
                  const success = await copyEmailAddress();
                  if (success) {
                    setSnackbarMessage('Email address copied to clipboard!');
                    setShowEmailDialog(false);
                  }
                }}>
                  Copy Address
                </Button>
                <Button onPress={() => setShowEmailDialog(false)}>Close</Button>
              </>
            ) : (
              <>
                <Button onPress={() => setShowEmailDialog(false)}>Cancel</Button>
                <Button 
                  onPress={async () => {
                    const id = await generateEmailInboxId();
                    if (id) {
                      setSnackbarMessage('Email address created successfully!');
                    }
                  }}
                  disabled={isGenerating}
                >
                  Generate
                </Button>
              </>
            )}
          </Dialog.Actions>
        </Dialog>
        {/* Calendar Feed Dialog */}
        <Dialog visible={showCalendarDialog} onDismiss={() => setShowCalendarDialog(false)}>
          <Dialog.Title>Calendar Feed</Dialog.Title>
          <Dialog.Content>
            <Text>Subscribe to this URL in your calendar app to automatically sync all your trips:</Text>
            <View style={styles.emailAddressContainer}>
              <ScrollView horizontal>
                <Text style={styles.calendarUrl}>{calendarFeedUrl}</Text>
              </ScrollView>
              <IconButton
                icon="content-copy"
                size={20}
                style={styles.copyIconInDialog}
                onPress={async () => {
                  const success = await copyCalendarUrl();
                  if (success) {
                    setSnackbarMessage('Calendar feed URL copied!');
                    setShowCalendarDialog(false);
                  }
                }}
              />
            </View>
            <Text style={styles.helpText}>
              How to subscribe:
            </Text>
            <Text style={styles.instructionText}>
              • <Text style={styles.bold}>Apple Calendar:</Text> File → New Calendar Subscription
            </Text>
            <Text style={styles.instructionText}>
              • <Text style={styles.bold}>Google Calendar:</Text> Other calendars → From URL
            </Text>
            <Text style={styles.instructionText}>
              • <Text style={styles.bold}>Outlook:</Text> Add calendar → Subscribe from web
            </Text>
            <Text style={[styles.helpText, { marginTop: 12 }]}>
              Your calendar will automatically update when you add or modify trips.
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={async () => {
              const success = await copyCalendarUrl();
              if (success) {
                setSnackbarMessage('Calendar feed URL copied!');
                setShowCalendarDialog(false);
              }
            }}>
              Copy URL
            </Button>
            <Button onPress={() => setShowCalendarDialog(false)}>Close</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar
        visible={!!snackbarMessage}
        onDismiss={() => setSnackbarMessage('')}
        duration={3000}
      >
        {snackbarMessage}
      </Snackbar>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  section: {
    backgroundColor: 'white',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    textTransform: 'uppercase',
  },
  signOutContainer: {
    padding: 16,
  },
  signOutButton: {
    borderColor: '#ff0000',
  },
  emailAddress: {
    fontSize: 16,
    fontWeight: 'bold',
    flex: 1,
  },
  emailAddressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 8,
    padding: 12,
    backgroundColor: '#f0f0f0',
    borderRadius: 4,
  },
  copyIconInDialog: {
    margin: 0,
  },
  emailHelperText: {
    fontSize: 12,
    color: '#666',
    paddingHorizontal: 16,
    paddingBottom: 8,
    paddingLeft: 72,
  },
  helpText: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
  },
  progressBar: {
    marginTop: 16,
  },
  calendarUrl: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#333',
  },
  instructionText: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
    paddingLeft: 8,
  },
  bold: {
    fontWeight: 'bold',
  },
});